import { motion, useScroll, useTransform } from 'framer-motion';
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';

const platforms = [
  { name: 'Shopify', icon: 'https://cdn.worldvectorlogo.com/logos/shopify.svg' },
  { name: 'Make', icon: 'https://images.ctfassets.net/un655fb9wln6/3xu9WYYJyMScG7FKnuVd1V/c4072d425c64525ea94ae9b60093fbaa/Make-Icon-Circle-Purple.svg' },
  { name: 'Zapier', icon: 'https://cdn.worldvectorlogo.com/logos/zapier-1.svg' },
  { name: 'OpenAI', icon: 'https://cdn.worldvectorlogo.com/logos/openai-2.svg' },
  { name: 'Meta', icon: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg' },
  { name: 'Google', icon: 'https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg' },
  { name: 'TikTok', icon: 'https://cdn.worldvectorlogo.com/logos/tiktok-1.svg' },
  { name: 'Stripe', icon: 'https://cdn.worldvectorlogo.com/logos/stripe-4.svg' }
];

const automationSteps = [
  {
    title: 'Ajout Produit',
    description: 'Un nouveau produit est ajouté sur votre boutique en ligne',
    icon: '📦',
    platforms: ['Shopify', 'WooCommerce'],
    stats: { value: '10+', label: 'Produits/Jour' }
  },
  {
    title: 'Génération de Contenu',
    description: 'L\'IA crée automatiquement des descriptions et visuels optimisés',
    icon: '🤖',
    platforms: ['GPT-4', 'DALL·E'],
    stats: { value: '50+', label: 'Visuels/Jour' }
  },
  {
    title: 'Création Campagnes',
    description: 'Déploiement automatique sur les réseaux publicitaires',
    icon: '📈',
    platforms: ['Meta Ads', 'Google Ads'],
    stats: { value: '14%', label: 'CTR Moyen' }
  },
  {
    title: 'SEO & Content',
    description: 'Articles de blog et optimisation SEO automatisés',
    icon: '📝',
    platforms: ['WordPress', 'GPT-4'],
    stats: { value: '30+', label: 'Articles/Mois' }
  },
  {
    title: 'Email Marketing',
    description: 'Séquences d\'emails personnalisées',
    icon: '📧',
    platforms: ['MailerLite', 'ActiveCampaign'],
    stats: { value: '45%', label: 'Taux d\'ouverture' }
  },
  {
    title: 'Influence & UGC',
    description: 'Collaboration automatisée avec les créateurs',
    icon: '🎯',
    platforms: ['Creator Marketplace', 'TikTok'],
    stats: { value: '200+', label: 'Créateurs/Mois' }
  }
];

const TimelineStep = ({ day, title, stats }: { day: number; title: string; stats: { label: string; value: string }[] }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 0.3]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  return (
    <motion.div
      ref={ref}
      style={{ opacity, scale }}
      className="relative flex items-center gap-8"
    >
      <div className="flex-shrink-0 w-32 text-right">
        <span className="text-neon-orange font-semibold">Jour {day}</span>
      </div>
      
      <div className="w-4 h-4 rounded-full bg-neon-orange flex-shrink-0 relative">
        <motion.div
          className="absolute w-full h-full rounded-full bg-neon-orange"
          animate={{ scale: [1, 1.5, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <div className="absolute w-1 h-24 bg-neon-orange/20 left-1/2 -translate-x-1/2 top-4" />
      </div>
      
      <div className="flex-grow bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
        <h4 className="font-semibold text-lg mb-4">{title}</h4>
        <div className="grid grid-cols-2 gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
            >
              <motion.div
                className="text-2xl font-bold text-neon-orange"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {stat.value}
              </motion.div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const AIMarketingSection = () => {
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section ref={containerRef} className="py-20 relative overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      {/* Animated Background */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ y: backgroundY }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--neon-orange)_0%,_transparent_100%)] opacity-20" />
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2
            className="text-4xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Lancez votre marketing automatique
            <span className="bg-gradient-to-r from-neon-orange to-[#FF4500] bg-clip-text text-transparent"> piloté par l'IA</span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            De l'idée à l'exécution en temps réel : SEO, influence, media buying, tout est connecté.
          </motion.p>
        </motion.div>

        {/* Automation Flow */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {automationSteps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative group"
              onMouseEnter={() => setHoveredStep(index)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-neon-orange/10 to-transparent rounded-xl blur"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              />
              
              <div className="relative bg-white rounded-xl p-6 shadow-lg group-hover:shadow-xl transition-all duration-300">
                <motion.div
                  className="text-4xl mb-4"
                  animate={{ rotate: [0, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {step.icon}
                </motion.div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-600 mb-4">{step.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {step.platforms.map((platform) => (
                    <motion.span
                      key={platform}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-full"
                      whileHover={{ scale: 1.05 }}
                    >
                      {platform}
                    </motion.span>
                  ))}
                </div>

                <motion.div
                  className="flex items-center justify-between text-sm text-gray-500"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <span>{step.stats.value}</span>
                  <span>{step.stats.label}</span>
                </motion.div>

                {hoveredStep === index && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute inset-0 bg-neon-orange/10 rounded-xl backdrop-blur-sm flex items-center justify-center"
                  >
                    <div className="text-center p-6">
                      <div className="text-2xl font-semibold text-neon-orange mb-2">
                        Automatisé à 100%
                      </div>
                      <p className="text-gray-700">
                        Aucune intervention manuelle requise
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Platform Integrations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.h3
            className="text-2xl font-semibold mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Plateformes Connectées
          </motion.h3>
          
          <div className="flex flex-wrap justify-center gap-8">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{
                  scale: 1.1,
                  rotate: 360,
                  transition: { duration: 0.8 }
                }}
                className="w-16 h-16 relative group"
              >
                <img
                  src={platform.icon}
                  alt={platform.name}
                  className="w-full h-full object-contain"
                />
                <motion.div
                  className="absolute inset-0 bg-neon-orange/10 rounded-lg"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Timeline */}
        <div className="mb-20">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-2xl font-semibold text-center mb-12"
          >
            Résultats en Temps Réel
          </motion.h3>

          <div className="space-y-12">
            <TimelineStep
              day={1}
              title="Lancement Produit"
              stats={[
                { label: 'Produits Générés', value: '10+' },
                { label: 'Visuels Créés', value: '50+' }
              ]}
            />
            <TimelineStep
              day={2}
              title="Campagnes Actives"
              stats={[
                { label: 'CTR Moyen', value: '14%' },
                { label: 'CPC', value: '0.45€' }
              ]}
            />
            <TimelineStep
              day={3}
              title="Premiers Résultats"
              stats={[
                { label: 'ROI', value: '+220%' },
                { label: 'Conv. Rate', value: '9%' }
              ]}
            />
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mt-16"
        >
          <div className="max-w-3xl mx-auto mb-8">
            <motion.p
              className="text-xl font-semibold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Le trafic est la nouvelle monnaie.
              <span className="text-neon-orange"> Google et Facebook sont vos banques.</span>
            </motion.p>
            <motion.p
              className="text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Ne laissez pas passer l'opportunité de transformer votre business avec l'automatisation marketing.
            </motion.p>
          </div>

          <Link
            to="/reserver"
            className="inline-block bg-gradient-to-r from-neon-orange to-[#FF4500] text-white px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden group"
          >
            <span className="relative z-10">Réservez votre audit IA marketing gratuit</span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
              initial={{ x: "-100%" }}
              whileHover={{ x: "100%" }}
              transition={{ duration: 1 }}
            />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default AIMarketingSection;